import React, { useState, useEffect, useMemo } from 'react';
import { Clock, Metric, TrendCompare } from '../components/SharedComponents';
import { getBelaggning } from '../qlik';
import PaceView from './PaceView';
import BokningslageTable from './BokningslageTable';
import AktionsLista from './AktionsLista';

// ── datum-hjälp (UTC-noon-ankare → ingen dygnsrullning) ──────────
const iso = (d) => d.toISOString().slice(0, 10);
const parseISO = (s) => new Date(s + 'T12:00:00Z');
const addDays = (s, n) => { const d = parseISO(s); d.setUTCDate(d.getUTCDate() + n); return iso(d); };
const WD = ['Sön', 'Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör'];
const fmtShort = (s) => { const d = parseISO(s); return `${d.getUTCDate()}/${d.getUTCMonth() + 1}`; };
const fmtLong = (s) => { const d = parseISO(s); return `${WD[d.getUTCDay()]} ${d.getUTCDate()}/${d.getUTCMonth() + 1}`; };

// Vilka mått som är valbara per kategori
const METRICS = {
  hotell: [['pct', 'Beläggning %'], ['gastnatter', 'Gästnätter'], ['rumsnatter', 'Rumsnätter']],
  form:   [['objektnatter', 'Objektnätter']], // förmedling saknar gästdata i Visbook
  allt:   [['enheter', 'Belagda enheter'], ['gastnatter', 'Gästnätter']], // enheter = rum + objekt (egen kombinerad serie)
};
const ACCENT = '#1d6a5f';

// Kalender-heatmap: en ruta per dag, färgad efter värdets intensitet.
// Veckodags-kolumner (Mån–Sön). pct färgas på absolut %, annars relativt periodmax.
function HeatCal({ days, fmtVal, isPct }) {
  if (!days || !days.length) return null;
  const nums = days.map(d => d.value).filter(v => v != null && isFinite(v));
  const max = Math.max(...nums, isPct ? 1 : 1);
  const intensity = (v) => (v == null || !isFinite(v)) ? 0 : (isPct ? v : (max > 0 ? v / max : 0));
  const color = (t) => {
    if (!t || t <= 0) return ['var(--surface-2, #f3f1ec)', 'var(--text-secondary)'];
    if (t < 0.2) return ['#E1F5EE', '#0F6E56'];
    if (t < 0.4) return ['#9FE1CB', '#04342C'];
    if (t < 0.6) return ['#5DCAA5', '#04342C'];
    if (t < 0.8) return ['#1D9E75', '#ffffff'];
    return ['#0F6E56', '#ffffff'];
  };
  const wd = (ds) => (parseISO(ds).getUTCDay() + 6) % 7; // Mån=0
  const cells = [];
  for (let i = 0; i < wd(days[0].datum); i++) cells.push(null); // tomma rutor före första dagen
  days.forEach(d => cells.push(d));
  const WDH = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];
  return (
    <div style={{ fontFamily: 'system-ui,sans-serif' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4, marginBottom: 4 }}>
        {WDH.map(h => <div key={h} style={{ fontSize: 11, color: 'var(--text-secondary)', textAlign: 'center' }}>{h}</div>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 4 }}>
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const dt = parseISO(d.datum);
          const [bg, fg] = color(intensity(d.value));
          return (
            <div key={i} title={`${fmtLong(d.datum)} · ${fmtVal(d.value)} (fjol ${fmtVal(d.prev)})`}
              style={{ aspectRatio: '1.6 / 1', background: bg, color: fg, borderRadius: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1.1 }}>
              <span style={{ fontSize: 10, opacity: 0.7 }}>{dt.getUTCDate()}/{dt.getUTCMonth() + 1}</span>
              <span style={{ fontSize: 12.5, fontWeight: 600 }}>{fmtVal(d.value)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function BelaggningBlock() {
  const [data, setData]   = useState(null);
  const [kat, setKat]     = useState('hotell');      // 'hotell' | 'form' | 'allt'
  const [metric, setMetric] = useState('pct');
  const [preset, setPreset] = useState('5v');
  const [from, setFrom]   = useState(null);
  const [to, setTo]       = useState(null);
  const [view2, setView2] = useState('linje'); // 'linje' | 'heatmap'
  const [mode, setMode]   = useState('oversikt'); // 'oversikt' | 'pace'
  const [inklOffert, setInklOffert] = useState(false); // rum: bekräftat vs inkl. offert (gruppförfrågningar)

  useEffect(() => { getBelaggning().then(setData).catch(() => setData({ byDatum: {}, totalRooms: 45, today: null })); }, []);

  const today = data?.today || iso(new Date());

  // Sätt standardperiod (5v: 1 bak + 4 fram) när data laddats
  useEffect(() => {
    if (data && from === null) { setFrom(addDays(today, -7)); setTo(addDays(today, 28)); }
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPreset = (p) => {
    setPreset(p);
    if (p === '5v')    { setFrom(addDays(today, -7));  setTo(addDays(today, 28)); }
    if (p === 'fram8') { setFrom(today);               setTo(addDays(today, 55)); }
    if (p === 'bak30') { setFrom(addDays(today, -30)); setTo(today); }
  };

  // Byt mått till kategorins default när kategori ändras
  const changeKat = (k) => {
    setKat(k);
    const allowed = METRICS[k].map(m => m[0]);
    if (!allowed.includes(metric)) setMetric(METRICS[k][0][0]);
  };

  const totalRooms = data?.totalRooms || 45;
  const byDatum = data?.byDatum || {};

  // Rumsvärden bekräftat vs inkl. offert (offerter, t.ex. gruppförfrågningar,
  // syns annars inte alls här — bara i Bokningsläge/Bokningstakt)
  const rumOf  = (rec) => inklOffert ? (rec.rumRumO ?? rec.rumRum) : rec.rumRum;
  const rumGOf = (rec) => inklOffert ? (rec.rumGO ?? rec.rumG) : rec.rumG;

  // Värde för en dag givet kategori + mått
  const valOf = (rec, m) => {
    if (!rec) return 0;
    if (m === 'pct')          return rumOf(rec) / totalRooms;
    if (m === 'rumsnatter')   return rumOf(rec);
    if (m === 'objektnatter') return rec.formObj;
    if (m === 'enheter')      return rumOf(rec) + rec.formObj; // belagda enheter = rum + objekt
    if (m === 'gastnatter')   return kat === 'hotell' ? rumGOf(rec) : kat === 'form' ? rec.formG : rumGOf(rec) + rec.formG;
    return 0;
  };

  // Bygg dagserie + aggregat (rumRum/rumG följer offert-växlaren; rumConf = alltid bekräftat, för ADR)
  const { days, agg } = useMemo(() => {
    const out = [];
    const a = { rumRum: 0, rumG: 0, rumConf: 0, formObj: 0, formG: 0, logi: 0, fRumRum: 0, fRumG: 0, fRumConf: 0, fFormObj: 0, fFormG: 0, fLogi: 0, n: 0 };
    if (!from || !to) return { days: out, agg: a };
    let cur = from;
    let guard = 0;
    while (cur <= to && guard++ < 800) {
      const rec = byDatum[cur];
      const frec = byDatum[addDays(cur, -364)]; // fjol, samma veckodag
      out.push({ datum: cur, value: valOf(rec, metric), prev: valOf(frec, metric) });
      a.n++;
      if (rec)  { a.rumRum += rumOf(rec);   a.rumG += rumGOf(rec);   a.rumConf += rec.rumRum;   a.formObj += rec.formObj;   a.formG += rec.formG;   a.logi  += rec.logi; }
      if (frec) { a.fRumRum += rumOf(frec); a.fRumG += rumGOf(frec); a.fRumConf += frec.rumRum; a.fFormObj += frec.formObj; a.fFormG += frec.formG; a.fLogi += frec.logi; }
      cur = addDays(cur, 1);
    }
    return { days: out, agg: a };
  }, [from, to, byDatum, kat, metric, totalRooms, inklOffert]); // eslint-disable-line react-hooks/exhaustive-deps

  const isPct = metric === 'pct';
  const chartData = days.map(d => ({ label: fmtShort(d.datum), value: d.value, prev: d.prev }));
  const fmtVal = isPct ? (v) => Math.round(v * 100) + '%' : (v) => Math.round(v).toLocaleString('sv-SE');

  // KPI-hjälp
  const pctStr = (n) => n != null && isFinite(n) ? Math.round(n * 100) + '%' : '–';
  const deltaPctSub = (cur, prev) => {
    if (!prev) return prev === 0 && cur > 0 ? `i fjol 0` : 'ingen fjoldata';
    const d = Math.round((cur - prev) / Math.abs(prev) * 100);
    return `${d >= 0 ? '+' : ''}${d}% mot fjol (${Math.round(prev).toLocaleString('sv-SE')})`;
  };
  const trendOf = (cur, prev) => prev ? (cur >= prev ? 'up' : 'down') : '';

  // Periodaggregat
  const days_n = agg.n || 1;
  const belPct   = agg.rumRum  / (totalRooms * days_n);
  const fBelPct  = agg.fRumRum / (totalRooms * days_n);
  const belDeltaPp = Math.round((belPct - fBelPct) * 100);
  const gn  = kat === 'hotell' ? agg.rumG : kat === 'form' ? agg.formG : agg.rumG + agg.formG;
  const fgn = kat === 'hotell' ? agg.fRumG : kat === 'form' ? agg.fFormG : agg.fRumG + agg.fFormG;

  // Vilka KPI-kort som visas per kategori
  const kpis = [];
  if (kat === 'hotell') {
    kpis.push({ label: 'Beläggning (period)', value: pctStr(belPct), sub: `${belDeltaPp >= 0 ? '+' : ''}${belDeltaPp} pp mot fjol (${pctStr(fBelPct)})`, trend: belPct >= fBelPct ? 'up' : 'down' });
  }
  if (kat === 'allt') { // kombinerad headline: rum + objekt
    const enh = agg.rumRum + agg.formObj, fEnh = agg.fRumRum + agg.fFormObj;
    kpis.push({ label: 'Belagda enheter', value: Math.round(enh).toLocaleString('sv-SE'), sub: deltaPctSub(enh, fEnh), trend: trendOf(enh, fEnh) });
  }
  if (kat !== 'form') { // förmedling saknar gästdata
    kpis.push({ label: 'Gästnätter', value: Math.round(gn).toLocaleString('sv-SE'), sub: deltaPctSub(gn, fgn), trend: trendOf(gn, fgn) });
  }
  if (kat === 'hotell' || kat === 'allt') {
    kpis.push({ label: 'Rumsnätter', value: Math.round(agg.rumRum).toLocaleString('sv-SE'), sub: deltaPctSub(agg.rumRum, agg.fRumRum), trend: trendOf(agg.rumRum, agg.fRumRum) });
  }
  if (kat === 'form' || kat === 'allt') {
    kpis.push({ label: 'Objektnätter', value: Math.round(agg.formObj).toLocaleString('sv-SE'), sub: deltaPctSub(agg.formObj, agg.fFormObj), trend: trendOf(agg.formObj, agg.fFormObj) });
  }
  if (kat === 'hotell') { // ADR + RevPAR (logi-intäkt / sålda resp. disponibla rumsnätter)
    // ADR alltid på BEKRÄFTADE rumsnätter — offerter har ingen bokförd logi-intäkt
    const kr = (v) => Math.round(v).toLocaleString('sv-SE') + ' kr';
    const adr     = agg.rumConf  > 0 ? agg.logi  / agg.rumConf  : 0;
    const fAdr    = agg.fRumConf > 0 ? agg.fLogi / agg.fRumConf : 0;
    const revpar  = agg.logi  / (totalRooms * days_n);
    const fRevpar = agg.fLogi / (totalRooms * days_n);
    kpis.push({ label: 'ADR (snitt/rumsnatt)', value: adr > 0 ? kr(adr) : '–', sub: deltaPctSub(adr, fAdr), trend: trendOf(adr, fAdr) });
    kpis.push({ label: 'RevPAR (per disp. rum)', value: revpar > 0 ? kr(revpar) : '–', sub: deltaPctSub(revpar, fRevpar), trend: trendOf(revpar, fRevpar) });
  }

  const chip = (active, label, onClick) => (
    <button onClick={onClick} style={{
      fontSize: 12.5, fontWeight: 600, padding: '6px 14px', borderRadius: 999,
      border: '1px solid ' + (active ? ACCENT : 'var(--border)'),
      background: active ? ACCENT : 'transparent', color: active ? '#fff' : 'var(--text-secondary)',
      cursor: 'pointer', whiteSpace: 'nowrap',
    }}>{label}</button>
  );

  return (
    <div className="screen">
      <div className="header header-belaggning">
        <div>
          <div className="header-title">Beläggning & bokningsläge</div>
          <div className="header-name">Den ledande indikatorn — i år vs fjolåret</div>
        </div>
        <Clock />
      </div>

      {/* Handlingslista: kommande veckor som släpar mot fjolåret */}
      {data && <AktionsLista byDatum={byDatum} totalRooms={totalRooms} paceLive={data?.paceLive} />}

      {/* Lägesväxlare: översikt (Qlik live) vs bokningstakt (pace-historik) */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {chip(mode === 'oversikt', 'Översikt', () => setMode('oversikt'))}
        {chip(mode === 'bokningslage', 'Bokningsläge', () => setMode('bokningslage'))}
        {chip(mode === 'pace', 'Bokningstakt', () => setMode('pace'))}
      </div>

      {mode === 'pace' ? (
        <PaceView paceLive={data?.paceLive} />
      ) : mode === 'bokningslage' ? (
        !data ? <div className="card" style={{ textAlign: 'center', padding: 32, color: '#888' }}>Hämtar beläggningsdata…</div>
              : <BokningslageTable byDatum={byDatum} totalRooms={totalRooms} paceLive={data?.paceLive} />
      ) : !data ? (
        <div className="card" style={{ textAlign: 'center', padding: 32, color: '#888' }}>Hämtar beläggningsdata…</div>
      ) : (
        <>
          {/* Filter */}
          <div className="card">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>Kategori</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {chip(kat === 'hotell', 'Hotell', () => changeKat('hotell'))}
                  {chip(kat === 'form',   'Förmedling', () => changeKat('form'))}
                  {chip(kat === 'allt',   'Allt', () => changeKat('allt'))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>Mått</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {METRICS[kat].map(([id, lbl]) => chip(metric === id, lbl, () => setMetric(id)))}
                </div>
              </div>
              {kat !== 'form' && (
                <div>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>Bokningar</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {chip(!inklOffert, 'Bekräftat', () => setInklOffert(false))}
                    {chip(inklOffert, 'Inkl. offert', () => setInklOffert(true))}
                  </div>
                </div>
              )}
              <div>
                <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>Period</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  {chip(preset === '5v',    '5 v (1 bak/4 fram)', () => applyPreset('5v'))}
                  {chip(preset === 'fram8', 'Framåt 8 v', () => applyPreset('fram8'))}
                  {chip(preset === 'bak30', 'Bakåt 30 d', () => applyPreset('bak30'))}
                  <input type="date" value={from || ''} max={to || undefined}
                    onChange={e => { setFrom(e.target.value); setPreset('custom'); }}
                    style={{ fontSize: 12.5, padding: '5px 8px', borderRadius: 8, border: '1px solid var(--border)' }} />
                  <span style={{ color: 'var(--text-secondary)' }}>–</span>
                  <input type="date" value={to || ''} min={from || undefined}
                    onChange={e => { setTo(e.target.value); setPreset('custom'); }}
                    style={{ fontSize: 12.5, padding: '5px 8px', borderRadius: 8, border: '1px solid var(--border)' }} />
                </div>
              </div>
            </div>
          </div>

          {/* KPI:er */}
          {/* auto-fit: 5 i bredd på desktop, 2 per rad på mobil — ingen sidscroll */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 12 }}>
            {kpis.map((k, i) => <Metric key={i} label={k.label} value={k.value} sub={k.sub} trend={k.trend} />)}
          </div>

          {/* Trendgraf / heatmap */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div className="card-title" style={{ margin: 0 }}>
                {METRICS[kat].find(m => m[0] === metric)?.[1]} per dag — {fmtLong(from)} → {fmtLong(to)}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                {chip(view2 === 'linje', 'Linje', () => setView2('linje'))}
                {chip(view2 === 'heatmap', 'Heatmap', () => setView2('heatmap'))}
              </div>
            </div>
            {view2 === 'linje'
              ? <TrendCompare data={chartData} formatVal={fmtVal} curColor={ACCENT} prevColor="#C2A878" />
              : <HeatCal days={days} fmtVal={fmtVal} isPct={isPct} />}
            <div className="note" style={{ marginTop: 6 }}>
              Källa: Visbook (Bokningar-appen) · fjolår = samma veckodag (−364 dgr).
              {inklOffert && kat !== 'form' ? ' INKL. OFFERT: även obekräftade offerter (t.ex. gruppförfrågningar) räknas med.' : ''}
              {view2 === 'heatmap' ? ' Mörkare ruta = högre värde.' : ''}
              {to > today ? ' OBS: framtida dagar fylls fortfarande på — fjolårsskuggan visar fjolårets slutliga utfall.' : ''}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
